import { useState, useRef, useCallback, useEffect } from 'react'

/**
 * Interface matching the legacy AutocompletePrediction structure for backward compatibility
 * while using the new Google Maps Places API internally
 */
export interface PlacePredictionCompat {
  description: string
  place_id: string
  placeId: string
  structured_formatting?: {
    main_text: string
    secondary_text: string
  }
  types?: string[]
  matched_substrings?: Array<{
    length: number
    offset: number
  }>
}

interface UsePlacesAutocompleteOptions {
  apiKey?: string
  debounce?: number
}

interface GetPredictionsRequest {
  input: string
  componentRestrictions?: {
    country: string | string[]
  }
  types?: string[]
  locationBias?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral
  locationRestriction?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral
}

interface UsePlacesAutocompleteReturn {
  placePredictions: PlacePredictionCompat[]
  getPlacePredictions: (request: GetPredictionsRequest) => void
  isPlacePredictionsLoading: boolean
  sessionToken: google.maps.places.AutocompleteSessionToken | null
  resetSessionToken: () => void
}

/**
 * Custom hook for Google Places Autocomplete using the new Autocomplete Data API
 * This replaces the deprecated AutocompleteService.getPlacePredictions()
 *
 * @see https://developers.google.com/maps/documentation/javascript/place-autocomplete-data
 */
export const usePlacesAutocomplete = (
  options: UsePlacesAutocompleteOptions = {}
): UsePlacesAutocompleteReturn => {
  const { debounce = 300 } = options

  const [placePredictions, setPlacePredictions] = useState<PlacePredictionCompat[]>([])
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false)
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null)

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const requestIdRef = useRef(0)

  // Initialize session token on mount -- poll until Google Maps Places is available
  useEffect(() => {
    if (typeof google !== 'undefined' && google.maps?.places?.AutocompleteSessionToken) {
      setSessionToken(new google.maps.places.AutocompleteSessionToken())
      return undefined
    }

    const interval = setInterval(() => {
      if (typeof google !== 'undefined' && google.maps?.places?.AutocompleteSessionToken) {
        setSessionToken(new google.maps.places.AutocompleteSessionToken())
        clearInterval(interval)
      }
    }, 200)

    return () => clearInterval(interval)
  }, [])

  const resetSessionToken = useCallback(() => {
    if (typeof google !== 'undefined' && google.maps?.places?.AutocompleteSessionToken) {
      setSessionToken(new google.maps.places.AutocompleteSessionToken())
    }
  }, [])

  /**
   * Convert new PlacePrediction to legacy-compatible format
   */
  const convertToCompatFormat = (
    placePrediction: google.maps.places.PlacePrediction
  ): PlacePredictionCompat => {
    const placeId = placePrediction.placeId || ''

    return {
      description: placePrediction.text?.text || '',
      place_id: placeId,
      placeId: placeId,
      structured_formatting: {
        main_text: placePrediction.mainText?.text || '',
        secondary_text: placePrediction.secondaryText?.text || ''
      },
      types: placePrediction.types || [],
      matched_substrings: placePrediction.text?.matches?.map(match => ({
        length: match.endOffset - match.startOffset,
        offset: match.startOffset
      }))
    }
  }

  /**
   * Map legacy type names to new type names if needed
   * The new API uses the same type names, so this is mostly a pass-through
   */
  const mapTypes = (types?: string[]): string[] | undefined => {
    if (!types || types.length === 0) return undefined
    // The new API accepts the same type strings
    return types
  }

  const getPlacePredictions = useCallback(
    (request: GetPredictionsRequest) => {
      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Don't search for empty input
      if (!request.input || request.input.trim() === '') {
        setPlacePredictions([])
        setIsPlacePredictionsLoading(false)
        return
      }

      setIsPlacePredictionsLoading(true)

      // Increment request ID to track the latest request
      const currentRequestId = ++requestIdRef.current

      debounceTimerRef.current = setTimeout(async () => {
        try {
          // Check if the new API is available
          if (
            typeof google === 'undefined' ||
            !google.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions
          ) {
            console.warn('Google Maps Places API (new) not available')
            setIsPlacePredictionsLoading(false)
            return
          }

          // Build the request for the new API
          const autocompleteRequest: google.maps.places.AutocompleteRequest = {
            input: request.input,
            sessionToken: sessionToken || undefined,
            includedPrimaryTypes: mapTypes(request.types),
            includedRegionCodes: request.componentRestrictions?.country
              ? Array.isArray(request.componentRestrictions.country)
                ? request.componentRestrictions.country
                : [request.componentRestrictions.country]
              : undefined,
            locationBias: request.locationBias,
            locationRestriction: request.locationRestriction
          }

          // Use the new Autocomplete Data API
          const { suggestions } =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(autocompleteRequest)

          // Ignore stale responses from superseded requests
          if (currentRequestId !== requestIdRef.current) return

          // Convert to backward-compatible format
          const compatPredictions: PlacePredictionCompat[] = suggestions
            .filter(suggestion => suggestion.placePrediction)
            .map(suggestion => convertToCompatFormat(suggestion.placePrediction!))

          setPlacePredictions(compatPredictions)
        } catch (error) {
          // Ignore errors from stale requests
          if (currentRequestId !== requestIdRef.current) return
          console.error('Error fetching autocomplete suggestions:', error)
          setPlacePredictions([])
        } finally {
          setIsPlacePredictionsLoading(false)
        }
      }, debounce)
    },
    [debounce, sessionToken]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      // Invalidate any in-flight requests so stale responses are ignored
      requestIdRef.current++
    }
  }, [])

  return {
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading,
    sessionToken,
    resetSessionToken
  }
}

export default usePlacesAutocomplete
